import type { FieldProp } from 'react-typed-form';

import * as Sentry from '@sentry/browser';
import * as React from 'react';

import { useApiCallable } from '../../api';
import { namePastedFile } from '../../util';

export const BUSY = '_busy';

type Upload = Readonly<{
  file: File;
  iri: string | null;
}>;

export type UploadsController = Readonly<{
  uploads: ReadonlyArray<Upload>;
  errorList: string[];
  isBusy: boolean;
  isLoading: boolean;
  addFiles: (files: File[]) => boolean;
  addPastedFiles: (files: File[]) => boolean;
  removeUpload: (index: number) => void;
}>;

export default function useUploads(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  field: FieldProp<any>
): UploadsController {
  const callApi = useApiCallable();

  const [uploads, setUploads] = React.useState<ReadonlyArray<Upload>>([]);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  // Held in a ref so that a batch started while another is still uploading
  // reads the other's progress instead of a stale snapshot.
  const list = React.useRef<ReadonlyArray<Upload>>([]);
  const batchesInFlight = React.useRef(0);
  const pastedCount = React.useRef(0);

  const setList = (newList: ReadonlyArray<Upload>) => {
    list.current = newList;
    setUploads(newList);
  };

  const uploadFiles = async (acceptedFiles: File[]) => {
    setUploadError(null);
    batchesInFlight.current += 1;
    field.handleValueChange(BUSY);
    setList([
      ...list.current,
      ...acceptedFiles.map((file) => ({ file, iri: null })),
    ]);

    try {
      for (const file of acceptedFiles) {
        const uploadResult = (
          await callApi('/uploads', {
            method: 'POST',
            uploadFile: file,
          })
        ).data as Record<string, string>;
        setList(
          list.current.map((upload) =>
            upload.file === file ? { file, iri: uploadResult['@id'] } : upload
          )
        );
      }
    } catch (err) {
      Sentry.captureException(err);
      setList(
        list.current.filter(
          (upload) => upload.iri || !acceptedFiles.includes(upload.file)
        )
      );
      setUploadError('Upload failed. Please try again.');
    }

    batchesInFlight.current -= 1;
    if (batchesInFlight.current === 0) {
      field.handleValueChange(list.current.map((upload) => upload.iri));
    }
  };

  // False when the files were not taken, so that a caller consuming an event
  // can let it through instead.
  const addFiles = (files: File[]): boolean => {
    if (field.isLoading) return false;
    void uploadFiles(files);
    return true;
  };

  const addPastedFiles = (files: File[]) =>
    addFiles(
      files.map((file) => {
        pastedCount.current += 1;
        return namePastedFile(file, pastedCount.current);
      })
    );

  const removeUpload = (index: number) => {
    const newList = list.current.filter((_, idx) => idx !== index);
    setList(newList);
    field.handleValueChange(newList.map((upload) => upload.iri));
  };

  return {
    uploads,
    errorList: uploadError ? [uploadError] : field.lastErrorList ?? [],
    isBusy: field.value === BUSY,
    isLoading: field.isLoading ?? false,
    addFiles,
    addPastedFiles,
    removeUpload,
  };
}
